import { domain } from "@ventas/shared";
import type { PaymentMethod } from "../payment-methods/api";
import { formatARS, inputValueFromCents, parseArsInput } from "../../lib/money";

export type CollectionTarget =
  | { kind: "general" }
  | { kind: "sale"; saleId: number; pendingCents: number }
  | { kind: "allocations"; allocations: CollectionAllocationDraft[] };

export type CollectionAllocationDraft = {
  saleId: number;
  pendingCents: number;
  customerPaid: string;
  discount: string;
};

export type CollectionLineDraft = {
  id: string;
  paymentMethodId: number | null;
  customerPaid: string;
  netReceived: string;
  target: CollectionTarget;
  /**
   * Exact configured discount quoted by "Completar" from a known debt.
   * This is kept in cents so the reverse quote from received money cannot
   * introduce a rounding difference in the preview.
   */
  completedDiscountCents: number | null;
  manualDiscount: string;
  reason: string;
};

export type CollectionLinePreview = {
  customerPaidCents: number;
  discountCents: number;
  debtCancelledCents: number;
  netReceivedCents: number | null;
  commissionCents: number;
  recognition: "immediate" | "pending_net" | "batch";
  discountSource: "none" | "configured" | "manual";
  requiresManualReason: boolean;
};

/**
 * A collection can be explained with cent-level discounts (for example,
 * completing a $5 debt with a 10% discount). Keep cents visible whenever
 * they exist so the operator sees the exact amount that will be persisted,
 * while whole-peso collection amounts remain as compact as the rest of the UI.
 */
export function formatCollectionAmount(cents: number): string {
  return formatARS(cents, Math.abs(cents) % 100 !== 0);
}

export function emptyCollectionLine(target: CollectionTarget = { kind: "general" }): CollectionLineDraft {
  return {
    id: crypto.randomUUID(),
    paymentMethodId: null,
    customerPaid: "",
    netReceived: "",
    target,
    completedDiscountCents: null,
    manualDiscount: "",
    reason: "",
  };
}

/**
 * UI adapter only: parses operator text and delegates every financial formula
 * to the shared domain. The API validates the same facts before persisting.
 */
export function previewCollectionLine(
  row: CollectionLineDraft,
  method: PaymentMethod | undefined,
): { preview: CollectionLinePreview | null; error: string | null } {
  if (!method) return { preview: null, error: null };

  const paid = parseArsInput(row.customerPaid);
  if (!paid.ok || paid.cents <= 0) return { preview: null, error: paid.ok ? "El importe debe ser mayor a cero." : null };

  const net = row.netReceived.trim() ? parseArsInput(row.netReceived) : null;
  if (net && !net.ok) return { preview: null, error: net.message };
  if (net?.ok && !method.requiresNetReceived) return { preview: null, error: "Este medio no requiere neto acreditado." };

  // A general delivery is FIFO credit, not a commercial discount. Discounts
  // only make sense when this line is assigned to an actual pending sale.
  let discountCents = 0;
  let discountSource: CollectionLinePreview["discountSource"] = "none";
  if (row.target.kind !== "general") {
    const manual = row.manualDiscount.trim() ? parseArsInput(row.manualDiscount) : null;
    if (manual && !manual.ok) return { preview: null, error: manual.message };
    if (manual?.ok) {
      discountCents = manual.cents;
      discountSource = discountCents > 0 ? "manual" : "none";
    } else if (row.completedDiscountCents !== null) {
      discountCents = row.completedDiscountCents;
      discountSource = discountCents > 0 ? "configured" : "none";
    } else if (method.discountPct > 0) {
      const quote = domain.quotePaymentLineFromReceived({
        receivedCents: paid.cents,
        discountPct: method.discountPct,
      });
      discountCents = quote.discountCents;
      discountSource = discountCents > 0 ? "configured" : "none";
    }
  }

  try {
    const economics = domain.computePaymentLineEconomics({
      customerPaidCents: paid.cents,
      discountCents,
      requiresNetReceived: method.requiresNetReceived,
      settlesInBatch: method.settlesInBatch,
      ...(net?.ok ? { netReceivedCents: net.cents } : {}),
    });
    return {
      preview: {
        ...economics,
        discountSource,
        requiresManualReason: discountSource === "manual" && discountCents > 0,
      },
      error: null,
    };
  } catch {
    return { preview: null, error: "Revisá los importes de esta línea." };
  }
}

export function completeCollectionLine(
  row: CollectionLineDraft,
  method: PaymentMethod | undefined,
): CollectionLineDraft {
  if (!method || row.target.kind !== "sale" || row.target.pendingCents <= 0) return row;
  const quote = domain.quotePaymentLineFromDebt({
    debtCancelledCents: row.target.pendingCents,
    discountPct: method.discountPct,
  });
  return {
    ...row,
    customerPaid: inputValueFromCents(quote.receivedCents),
    completedDiscountCents: quote.discountCents,
    manualDiscount: "",
    reason: "",
  };
}

/**
 * Converts the explicit advanced-allocation inputs into the API shape. It
 * intentionally only checks conservation and per-sale limits: discounts and
 * payment economics are still quoted by the shared domain above and checked
 * again by the API.
 */
export function evaluateCollectionAllocations(
  row: CollectionLineDraft,
  preview: CollectionLinePreview | null,
): {
  allocations: Array<{ saleId: number; customerPaidAppliedCents: number; discountCents: number }>;
  error: string | null;
} {
  if (row.target.kind === "general") return { allocations: [], error: null };
  if (!preview) return { allocations: [], error: "Completá el importe antes de distribuirlo." };

  const raw = row.target.kind === "sale"
    ? [{ saleId: row.target.saleId, pendingCents: row.target.pendingCents, customerPaid: inputValueFromCents(preview.customerPaidCents), discount: inputValueFromCents(preview.discountCents) }]
    : row.target.allocations;
  const allocations: Array<{ saleId: number; customerPaidAppliedCents: number; discountCents: number }> = [];
  let paidTotal = 0;
  let discountTotal = 0;
  for (const allocation of raw) {
    const paid = allocation.customerPaid.trim() ? parseArsInput(allocation.customerPaid) : { ok: true as const, cents: 0 };
    const discount = allocation.discount.trim() ? parseArsInput(allocation.discount) : { ok: true as const, cents: 0 };
    if (!paid.ok) return { allocations: [], error: paid.message };
    if (!discount.ok) return { allocations: [], error: discount.message };
    if (paid.cents + discount.cents === 0) continue;
    try {
      domain.evaluateAccountSaleAllocation({
        outstandingCents: allocation.pendingCents,
        cashAppliedCents: paid.cents,
        discountCents: discount.cents,
        configuredDiscountCents: discount.cents,
      });
    } catch {
      return { allocations: [], error: `La asignación de la venta #${allocation.saleId} supera su pendiente.` };
    }
    paidTotal += paid.cents;
    discountTotal += discount.cents;
    allocations.push({ saleId: allocation.saleId, customerPaidAppliedCents: paid.cents, discountCents: discount.cents });
  }
  if (!allocations.length) return { allocations: [], error: "Indicá cómo se distribuye esta línea entre las ventas." };
  if (paidTotal !== preview.customerPaidCents || discountTotal !== preview.discountCents) {
    return { allocations: [], error: "Las asignaciones deben sumar exactamente lo recibido y el descuento de esta línea." };
  }
  return { allocations, error: null };
}

export function allocationTarget(pendingSales: Array<{ saleId: number; pendingCents: number }>): CollectionTarget {
  return {
    kind: "allocations",
    allocations: pendingSales.map((sale) => ({
      saleId: sale.saleId,
      pendingCents: sale.pendingCents,
      customerPaid: "",
      discount: "",
    })),
  };
}
